import {
  LocationI,
  LocationSearchResultI,
  MakeI,
  ModelI,
  MultiLevelNodeI,
  ResolvedLocationI,
  SubcategoryI
} from "../../typings/configurationTypes";
import {
  DataSource,
  HrCountryId,
  LocationLevel,
  LocationSearchFieldByLevel,
  NoneHrCountryId
} from "../../models/enums";

// Labels of the two country filters returned by configuration/locations/popular.
export const CountryFilterLabels = {
  [HrCountryId]: "Cijela Hrvatska",
  [NoneHrCountryId]: "Izvan Hrvatske"
};

// Makes are returned with `models`; subcategories with `children`.
// The pickers work on a single node shape.
export const makesToNodes = (makes: MakeI[] | undefined): MultiLevelNodeI[] =>
  (makes || []).map(make => ({
    ...make,
    children: make.models || []
  }));

export const subcategoriesToNodes = (
  subcategories: SubcategoryI[] | undefined
): MultiLevelNodeI[] => subcategories || [];

// Makes and models offered in any of the subcategories (categoryLv1Id). Nodes without
// subcategories (every category except commercial vehicles) are offered everywhere.
const offeredInSubcategories = (node: { categoryLv1Ids?: string[] }, subcategoryIds: string[]) =>
  !node.categoryLv1Ids ||
  node.categoryLv1Ids.length === 0 ||
  node.categoryLv1Ids.some(id => subcategoryIds.indexOf(id.toLowerCase()) !== -1);

export const filterMakesBySubcategories = (
  makes: MakeI[] | undefined,
  subcategoryIds: string[] | undefined
): MakeI[] | undefined => {
  if (!makes || !subcategoryIds || subcategoryIds.length === 0) {
    return makes;
  }

  const wanted = subcategoryIds.map(id => id.toLowerCase());

  return makes
    .filter(make => offeredInSubcategories(make, wanted))
    .map(make => ({
      ...make,
      models: (make.models || []).filter(model => offeredInSubcategories(model, wanted))
    }));
};

export const filterMakesBySubcategory = (
  makes: MakeI[] | undefined,
  subcategoryId: string | undefined
): MakeI[] | undefined => filterMakesBySubcategories(makes, subcategoryId ? [subcategoryId] : undefined);

export const findMake = (makes: MakeI[] | undefined, makeId: string): MakeI | undefined =>
  (makes || []).find(make => make.id === makeId);

export const findMakeByModel = (
  makes: MakeI[] | undefined,
  modelId: string
): MakeI | undefined =>
  (makes || []).find(make => (make.models || []).some(model => model.id === modelId));

export const findModel = (makes: MakeI[] | undefined, modelId: string): ModelI | undefined => {
  const make = findMakeByModel(makes, modelId);
  return make ? make.models.find(model => model.id === modelId) : undefined;
};

// Path (root -> node) to the node with the given id or route, or null.
export const findNodePath = (
  nodes: MultiLevelNodeI[] | undefined,
  idOrRoute: string,
  path: MultiLevelNodeI[] = []
): MultiLevelNodeI[] | null => {
  if (!nodes || !idOrRoute) {
    return null;
  }

  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    const currentPath = [...path, node];

    if (node.id === idOrRoute || node.route === idOrRoute) {
      return currentPath;
    }

    const found = findNodePath(node.children, idOrRoute, currentPath);
    if (found) {
      return found;
    }
  }

  return null;
};

export const findNode = (
  nodes: MultiLevelNodeI[] | undefined,
  idOrRoute: string
): MultiLevelNodeI | undefined => {
  const path = findNodePath(nodes, idOrRoute);
  return path ? path[path.length - 1] : undefined;
};

export const findNodeName = (nodes: MultiLevelNodeI[] | undefined, id: string) => {
  const node = findNode(nodes, id);
  return node ? node.name : undefined;
};

// "strucna/informatika" for the subcategory tree, "bmw/x5" for makes.
export const buildNodeRoutePath = (nodes: MultiLevelNodeI[] | undefined, id: string) => {
  const path = findNodePath(nodes, id);
  return path ? path.map(node => node.route).filter(Boolean).join("/") : null;
};

// Search query field for a location level (includeCountryIds, includeCountyIds, ...).
export const getLocationSearchField = (level: number) =>
  LocationSearchFieldByLevel[level];

export const getLocationLevelByField = (field: string) =>
  LocationSearchFieldByLevel.indexOf(field);

export const locationLevelKey = (level: number) =>
  level === LocationLevel.country
    ? "country"
    : level === LocationLevel.county
      ? "county"
      : level === LocationLevel.city
        ? "city"
        : "settlement";

// Full slug path of a search result / resolved location: "splitsko-dalmatinska/split/bacvice".
export const buildLocationRoutePath = (
  location: LocationSearchResultI | ResolvedLocationI | null | undefined
) => {
  if (!location) {
    return "";
  }

  // resolved locations carry the ready path
  const resolved: any = location;
  if (resolved.routePath) {
    return resolved.routePath;
  }

  const level = location.level;
  const parts = [];
  if (level >= LocationLevel.city) {
    parts.push(location.countyRoute);
  }
  if (level >= LocationLevel.settlement) {
    parts.push(location.cityRoute);
  }
  parts.push(location.route);

  return parts.filter(Boolean).join("/");
};

// Ancestor + own names of a resolved location, deepest last.
export const resolvedLocationNames = (location: ResolvedLocationI | null | undefined) =>
  location
    ? [location.countyName, location.cityName, location.settlementName].filter(Boolean)
    : [];

// Human readable address from an entity carrying the *Name fields the API now
// returns next to the location ids (ads, sellers, user addresses, chats).
// Croatian locations: "Settlement, City, County" (settlement only when requested).
// Foreign locations: country name.
export const formatLocationAddress = (
  entity: {
    countryId?: string;
    countryName?: string;
    countyName?: string;
    cityName?: string;
    settlementName?: string;
  } | null | undefined,
  options: { withSettlement?: boolean } = {}
) => {
  if (!entity) {
    return null;
  }

  if (entity.countryId && entity.countryId !== HrCountryId) {
    return entity.countryName || "";
  }

  const parts = [];
  if (options.withSettlement && entity.settlementName) {
    parts.push(entity.settlementName);
  }
  if (entity.cityName) {
    parts.push(entity.cityName);
  }
  if (entity.countyName) {
    parts.push(entity.countyName);
  }

  if (parts.length === 0 && entity.countryName) {
    return entity.countryName;
  }

  return parts.join(", ");
};

// Popular location ids (counties and cities) from configuration/locations/popular.
export const getPopularLocationIds = (popularLocations: LocationI[] | null | undefined) => {
  const ids: Set<string> = new Set();
  (popularLocations || []).forEach(location => {
    if (location.level !== LocationLevel.country) {
      ids.add(location.id);
    }
  });
  return ids;
};

export const isMakeDataSource = (dataSource: string) => dataSource === DataSource.make;
export const isLocationDataSource = (dataSource: string) =>
  dataSource === DataSource.location;



// WEBPACK FOOTER //
// ./src/routes/Configuration/helpers.ts